import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { createTeam } from '../../../../api/team'
import { displayErrorToast } from '../../../../helpers'
import StepFooter from '../StepFooter'
import { STEP_IDS } from '../onboardingSteps'
import { StepProps } from '../types'

const formSchema = z.object({
  name: z.string().min(2).max(20),
})

const TeamStep = ({
  guild,
  createdTeam,
  onTeamCreated,
  goNext,
  goBack,
  isFirst,
}: StepProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [submitting, setSubmitting] = useState(false)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { name: createdTeam?.name ?? 'Main' },
  })

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    if (createdTeam) {
      goNext()
      return
    }

    setSubmitting(true)
    createTeam(guild.id, { ...values, onboardingStep: STEP_IDS[1] })
      .then(async (response) => {
        await queryClient.invalidateQueries({
          queryKey: ['guild', { path: guild.path }],
        })
        setSubmitting(false)
        onTeamCreated(response.team)
      })
      .catch((error) => {
        displayErrorToast(error, t)
        setSubmitting(false)
      })
  }

  return (
    <div>
      <div className='mb-6 flex flex-col items-center gap-3 text-center'>
        <span className='flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary'>
          <Users className='h-6 w-6' />
        </span>
        <h2 className='text-2xl font-semibold'>Name your team</h2>
        <p className='max-w-md text-muted-foreground'>
          Each team in your guild can have its own separate wowaudit
          environment.
        </p>
      </div>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className='mx-auto max-w-sm'
        >
          <FormField
            control={form.control}
            name='name'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Team name</FormLabel>
                <FormControl>
                  <Input
                    placeholder='Team name'
                    disabled={!!createdTeam}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <StepFooter
            showBack={!isFirst}
            onBack={goBack}
            onContinue={form.handleSubmit(onSubmit)}
            continueLabel={createdTeam ? 'Continue' : 'Create team'}
            continueLoading={submitting}
          />
        </form>
      </Form>
    </div>
  )
}

export default TeamStep
